import { api } from "./api";

/**
 * v2 equivalent of v1's Firestore-based `generatedNotePresence`.
 *
 * v1 queried Firestore's `generatedNote` collection by `(id, type)` to decide Yes/No.
 * v2 calls the NestJS backend's `GET /:campus/clinical-artifacts?consultationId=&contextType=`,
 * which returns the same artifact (or 404 if none yet). Yes when `generatedNoteText` is non-empty.
 *
 * The v1 `kind` → Firestore `type` mapping is preserved (`psychiatrist→psychiatric`,
 * `evaluation_form→evaluationform`, etc.) and used here as the `contextType` value. The backend
 * stores artifacts by exactly this string. If your backend canonicalizes context types
 * differently (e.g. `INITIAL_CONSULTATION`), adjust the map below.
 */

const CONTEXT_TYPE_BY_KIND = {
  psychiatrist: "psychiatric",
  psychologist: "psychologist",
  evaluation_form: "evaluationform",
  special_educator: "special_educator",
  occupational_therapist: "occupational_therapist",
  speech_therapist: "speech_therapist",
  rehabilitation_psychologist: "rehabilitation_psychologist",
  pediatric_physiotherapist: "pediatric_physiotherapist",
};

/** Best-effort fetch; returns the artifact object or null on 404. */
async function fetchArtifactRaw(campus, consultationId, contextType) {
  try {
    const { data } = await api.get(`/${campus}/clinical-artifacts`, {
      params: { consultationId: Number(consultationId), contextType },
    });
    return data || null;
  } catch (err) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}

/**
 * Backwards-compatible signature: same args as v1, except the first parameter is the campus
 * slug instead of a Firestore `db`. We re-export the v1 name so the list section components
 * import path stays identical.
 *
 * @param {string} campus
 * @param {string|number} consultationId
 * @param {keyof CONTEXT_TYPE_BY_KIND} kind
 * @param {string|number|undefined} _patientIdForLegacy ignored (kept for arg parity)
 */
export async function getNotesAddedYesNo(campus, consultationId, kind, _patientIdForLegacy) {
  const ctx = CONTEXT_TYPE_BY_KIND[kind];
  if (!ctx) return "N/A";
  if (consultationId == null || consultationId === "") return "No";
  try {
    const raw = await fetchArtifactRaw(campus, consultationId, ctx);
    if (!raw) return "No";
    const body =
      typeof raw.generatedNoteText === "string" && raw.generatedNoteText.trim().length > 0;
    return body ? "Yes" : "No";
  } catch (e) {
    console.warn("[generatedNotePresence] lookup failed", { consultationId, kind, e });
    return "N/A";
  }
}

export function notesAddedDisplay(flag) {
  return flag === "Yes" ? "Yes" : "No";
}

export async function fetchGeneratedNoteDocument(campus, consultationId, kind, _patientId) {
  const ctx = CONTEXT_TYPE_BY_KIND[kind];
  if (!ctx) return null;
  const raw = await fetchArtifactRaw(campus, consultationId, ctx);
  if (!raw) return null;
  // Normalise to the v1 shape consumed by NoteModal: `{ id, type, note, created, updated, ... }`.
  return {
    docId: `${consultationId}:${ctx}`,
    id: consultationId,
    type: ctx,
    note: raw.generatedNoteText ?? raw.mscNoteText ?? raw.transcriptionText ?? "",
    created: raw.createdAt ?? null,
    updated: raw.updatedAt ?? null,
    /** v1's evaluation form role inference reads `physiotherapist_id` / `doctor_id` etc. — pass
     * the raw artifact metadata through so the caller can still inspect it. */
    ...(raw.generatedNoteMeta && typeof raw.generatedNoteMeta === "object"
      ? raw.generatedNoteMeta
      : {}),
  };
}
